import { useEffect, useRef } from "react";
import { useLocation, useRevalidator } from "react-router";

import CommentApi from "@/api/comment-api";
import CandidateCommentPostBox from "@/components/common/candidate-comment-post-box";
import Comment from "@/components/common/comment";
import { boxclass, scrollToBottom, cn, logRejection } from "@/helpers";
import type { Candidate } from "@/types/candidate";
import type { Comment as CommentType } from "@/types/comment";

interface CommentSectionProps {
  candidate: Candidate;
  comments: CommentType[];
}

export default function CommentSection({ candidate, comments }: CommentSectionProps) {
  const revalidator = useRevalidator();
  const { hash } = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hash === "#add-comment") {
      scrollToBottom(ref);
    }
  }, [hash]);

  const _postCallback = () => {
    void revalidator.revalidate();
    setTimeout(() => {
      scrollToBottom(ref);
    }, 100);
  };

  const _deleteComment = (comment_id: string) => {
    const abort_controller = new AbortController();
    CommentApi.deleteCandidateComment(comment_id, abort_controller.signal)
      .then(({ error }) => {
        if (error) {
          console.error(error);
        } else {
          void revalidator.revalidate();
        }
      })
      .catch(logRejection);
  };

  return (
    <div className={cn("mx-auto mb-8 max-w-[40rem] md:p-8", boxclass)} ref={ref}>
      {comments.map((c) => (
        <Comment key={c.id} comment={c} deleteFn={_deleteComment} />
      ))}
      <CandidateCommentPostBox candidate={candidate} postCallback={_postCallback} />
    </div>
  );
}
